import { isToday } from "date-fns";
import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router";

import ExpandableSection from "@components/animations/ExpandableSection";
import Button from "@components/buttons/Button";
import styles from "@components/carePlan/CarePlan.module.css";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DateStamp from "@components/dates/DateStamp";
import TimeRange from "@components/dates/TimeRange";
import ConsultUpdateForm from "@components/forms/ConsultUpdateForm";
import Modal from "@components/Modal";
import Popover from "@components/popovers/Popover";
import PopoverItem from "@components/popovers/PopoverItem";
import RescheduleSession from "@components/schedule/RescheduleSession";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { GetJourneyWithPlanQuery } from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { consultEndsInFuture } from "@utils/timeUtils";

type SessionProps = {
  consult: GetJourneyWithPlanQuery["journey"]["consults"][0];
  editingConsultId: string | null;
  index: number;
  journey: GetJourneyWithPlanQuery["journey"];
};

const Session = ({ consult, editingConsultId, index, journey }: SessionProps) => {
  const intl = useIntl();
  const popoverOpener = useOpener();
  const editConsultOpener = useOpener();
  const rescheduleOpener = useOpener(editingConsultId === consult.id);
  const myClient = useMyClient();
  const consultRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (rescheduleOpener.isOpen && consultRef.current && editingConsultId) {
      setTimeout(() => {
        consultRef.current?.scrollIntoView({ behavior: "smooth" });
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete("consultId");
        history.replace({
          hash: location.hash,
          search: searchParams.toString()
        });
      }, 500);
    }
  }, [editingConsultId, history, location.hash, rescheduleOpener.isOpen]);

  return (
    <>
      <div
        className={`${
          index % 2 === 0 ? styles.evenConsult : styles.oddConsult
        } ion-padding-top ion-padding-bottom flex items-center justify-between full-width`}
        key={consult.id}
        ref={consultRef}
      >
        <BodyCopy>
          <DateAndTimeStamp value={consult.startTime} />
        </BodyCopy>
        <div className="flex gap-md">
          {consultEndsInFuture(consult) && isToday(consult.startTime) && consult.joinUrl && (
            <div className={styles.joinSpacing}>
              <Button
                aria-label={intl.formatMessage({ id: "models.consult.join" })}
                href={consult.joinUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <IconBase icon="video" slot="icon-only" />
              </Button>
            </div>
          )}
          {consultEndsInFuture(consult) && (
            <div className="align-center">
              <Popover isOpen={popoverOpener.isOpen} opener={popoverOpener}>
                <PopoverItem
                  icon="calendar"
                  onClick={() => {
                    rescheduleOpener.open();
                    popoverOpener.close();
                  }}
                  textKey="pages.expertSection.reschedule"
                />
                <PopoverItem
                  icon="edit"
                  onClick={() => {
                    editConsultOpener.open();
                    popoverOpener.close();
                  }}
                  textKey="dictionary.edit"
                />
                <PopoverItem
                  icon="calendarXmark"
                  lines="none"
                  onClick={() => {
                    popoverOpener.close();
                    // This must be async in order to allow the popover to close before the route change
                    Promise.resolve().then(() => {
                      history.push(
                        journeyRoute({
                          journeyId: journey.id,
                          linkAfterJourney: `/consults/${consult.id}/cancel`,
                          myClient
                        })
                      );
                    });
                  }}
                  textKey="pages.consults.reschedule.updated.cancel"
                />
              </Popover>
            </div>
          )}
        </div>
      </div>
      <ExpandableSection isOpen={rescheduleOpener.isOpen} key="reschedule-session">
        <RescheduleSession consult={consult} handleClose={rescheduleOpener.close} journey={journey} />
      </ExpandableSection>
      <Modal opener={editConsultOpener} title={intl.formatMessage({ id: "models.consult.editConsult" })}>
        {() => (
          <>
            <div className={styles.intro}>
              <BodyCopy bold>
                <FormattedMessage id="models.consult.dateAndTime" />
              </BodyCopy>
            </div>
            <div>
              <BodyCopy>
                <div>
                  <DateStamp value={consult.startTime} />
                </div>
                <div>
                  <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
                </div>
              </BodyCopy>
            </div>

            <ConsultUpdateForm
              consult={consult}
              journey={journey}
              onCancel={editConsultOpener.close}
              opener={editConsultOpener}
            />
          </>
        )}
      </Modal>
    </>
  );
};

type Props = {
  editingConsultId: string | null;
  journey: GetJourneyWithPlanQuery["journey"];
};

const SessionHistory = ({ editingConsultId, journey }: Props) => {
  const consultsOrdered = sortByTimestamp(
    journey.consults.filter(c => !c.canceled),
    consult => consult.startTime
  );

  return (
    <div className="padding-40-bottom">
      {consultsOrdered.length > 0 ? (
        <>
          {consultsOrdered.map((consult, index) => (
            <Session
              consult={consult}
              editingConsultId={editingConsultId}
              index={index}
              journey={journey}
              key={consult.id}
            />
          ))}
        </>
      ) : (
        <FormattedMessage id="pages.consults.noSessionHistory" />
      )}
    </div>
  );
};

export default SessionHistory;
